import { fitTitle, fitDescription } from "@/lib/seo-text";
import { Link, createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Download, FileText, Phone } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { getAttribution } from "@/lib/attribution";

import { FIRM, FIRM_ADDRESS } from "@/lib/firm";
import { subscribeFollowUp } from "@/lib/follow-up.functions";

const CHECKLISTS = [
  {
    id: "retirement-village",
    title: "Retirement village purchase checklist",
    blurb: "Exit fees, recurring charges and the questions to ask before you sign a contract.",
    href: "/retirement-village-purchase-checklist.pdf",
    matters: ["elder-law"],
  },
  {
    id: "contested-estate",
    title: "Contested estate intake checklist",
    blurb: "What we need to assess a family provision application in Queensland.",
    href: "/contested-estate-intake-checklist.pdf",
    matters: ["wills-estates"],
  },
  {
    id: "granny-flat",
    title: "Granny flat arrangement questions",
    blurb: "Ten questions to work through before money changes hands within a family.",
    href: "/granny-flat-arrangement-questions-checklist.pdf",
    matters: ["granny-flat"],
  },
  {
    id: "business",
    title: "Buying or selling a business checklist",
    blurb: "The documents to request before settlement on a business or management rights.",
    href: "/business-sale-purchase-checklist.pdf",
    matters: ["business-commercial"],
  },
] as const;

const SUBMISSION_COPY: Record<string, { heading: string; body: string }> = {
  enquiry: {
    heading: "Thanks — your enquiry is with us",
    body: "We read every enquiry ourselves and usually reply the same business day.",
  },
  booking: {
    heading: "Thanks — your appointment request is with us",
    body: "We'll confirm your time by phone or email, usually within a few business hours.",
  },
  "granny-flat": {
    heading: "Thanks — your granny flat enquiry is with us",
    body: "We'll come back to you with the options for documenting the arrangement and a fixed fee where we can.",
  },
  executor: {
    heading: "Thanks — we have the estate details",
    body: "We'll review what you've told us and come back with whether a grant of probate is needed and the fixed fee, in writing.",
  },
  checklist: {
    heading: "Thanks — your checklist is ready",
    body: "Download it below. If anything raises a question, we're happy to talk it through.",
  },
};

const searchSchema = z.object({
  type: z.enum(["enquiry", "booking", "granny-flat", "executor", "checklist"]).catch("enquiry"),
  matter: z.string().max(60).optional().catch(undefined),
});

export const Route = createFileRoute("/thank-you")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary" },
      { title: fitTitle("Thank you — Coastside Law, Golden Beach") },
      {
        name: "description",
        content:
          fitDescription("Thanks for contacting Coastside Law in Golden Beach, Caloundra. Download a free Queensland conveyancing, estates or elder law checklist while you wait for our reply."),
      },
      { property: "og:title", content: fitTitle("Thank you — Coastside Law") },
      {
        property: "og:description",
        content:
          fitDescription("Your message has reached Coastside Law. Download a free Queensland legal checklist while you wait."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/thank-you" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/thank-you" }],
  }),
  component: ThankYouPage,
});

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";

function ThankYouPage() {
  const { type, matter } = Route.useSearch();
  const copy = SUBMISSION_COPY[type] ?? SUBMISSION_COPY["enquiry"]!;

  const primary =
    CHECKLISTS.find((c) => matter && (c.matters as readonly string[]).includes(matter)) ??
    CHECKLISTS.find((c) =>
      matter?.startsWith("conveyancing") ? c.id === "retirement-village" : false,
    ) ??
    CHECKLISTS[0];
  const others = CHECKLISTS.filter((c) => c.id !== primary.id);

  return (
    <main className="bg-background">
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center sm:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            Received
          </span>
          <h1 className="mt-6 font-serif text-3xl text-foreground sm:text-4xl">{copy.heading}</h1>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            {copy.body} If your matter is urgent, call{" "}
            <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
              {FIRM.phone}
            </a>
            .
          </p>
          <p className="mt-3 text-xs text-muted-foreground">{FIRM_ADDRESS}</p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-14">
        <h2 className="font-serif text-2xl text-foreground">
          While you wait — a checklist that suits your matter
        </h2>
        <div className="mt-6 rounded-sm border border-primary/30 bg-primary/5 p-6">
          <div className="flex items-start gap-3">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div>
              <h3 className="text-base font-semibold text-foreground">{primary.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{primary.blurb}</p>
              <a
                href={primary.href}
                download
                className="mt-4 inline-flex items-center gap-2 rounded-sm bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                <Download className="h-4 w-4" aria-hidden />
                Download the checklist (PDF)
              </a>
            </div>
          </div>
        </div>

        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {others.map((c) => (
            <li key={c.id} className="rounded-sm border border-border bg-card p-4">
              <p className="text-sm font-semibold text-foreground">{c.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{c.blurb}</p>
              <a
                href={c.href}
                download
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary underline"
              >
                <Download className="h-3.5 w-3.5" aria-hidden />
                Download PDF
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <FollowUpForm matter={matter} checklist={primary.title} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-14">
        <h2 className="font-serif text-2xl text-foreground">Next steps</h2>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <Link to="/fees" className="rounded-sm border border-border bg-card p-4 hover:border-primary">
            <p className="text-sm font-semibold text-foreground">Fixed fees</p>
            <p className="mt-1 text-xs text-muted-foreground">See what conveyancing costs with us.</p>
          </Link>
          <Link to="/guides" className="rounded-sm border border-border bg-card p-4 hover:border-primary">
            <p className="text-sm font-semibold text-foreground">Guides</p>
            <p className="mt-1 text-xs text-muted-foreground">Plain-English Queensland explainers.</p>
          </Link>
          <a
            href={FIRM.phoneHref}
            className="rounded-sm border border-border bg-card p-4 hover:border-primary"
          >
            <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Phone className="h-4 w-4 text-primary" aria-hidden />
              Call us
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{FIRM.phone}</p>
          </a>
        </div>
      </section>
    </main>
  );
}

function FollowUpForm({ matter, checklist }: { matter?: string | undefined; checklist: string }) {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);
    const email = String(fd.get("email") ?? "").trim();
    const name = String(fd.get("name") ?? "").trim();

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }
    setError(undefined);
    setSending(true);
    try {
      await subscribeFollowUp({
        data: { name, email, matterType: matter ?? "", checklist, ...getAttribution("/thank-you") } as never,
      });
      setDone(true);
      form.reset();
      toast.success("Thanks — we'll send you a follow-up.");
    } catch {
      toast.error(`Something went wrong. Please call us on ${FIRM.phone}.`);
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-sm border border-border bg-card p-8 text-center">
        <h2 className="font-serif text-2xl text-foreground">You're on the list</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We'll email you a short follow-up with the practical next steps for your matter. No
          marketing, and you can tell us to stop at any time.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-sm border border-border bg-card p-6 sm:p-8" noValidate>
      <h2 className="font-serif text-2xl text-foreground">Want a follow-up email?</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        We'll send you one short email with the next steps for your matter and a copy of the
        checklist link above.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-foreground" htmlFor="fu-name">
            Your name <span className="text-muted-foreground">(optional)</span>
          </label>
          <input id="fu-name" name="name" className={`${field} mt-2`} autoComplete="name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground" htmlFor="fu-email">
            Email
          </label>
          <input
            id="fu-email"
            name="email"
            type="email"
            className={`${field} mt-2`}
            autoComplete="email"
            required
          />
          {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
        </div>
      </div>
      <button
        type="submit"
        disabled={sending}
        className="mt-6 inline-flex w-full items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Saving…" : "Send me the follow-up"}
      </button>
    </form>
  );
}
